import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { setToken } from "../lib/nativeTokenStorage";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      navigate("/login");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        // P0 2026-09-30 — persist the Google session token in first-party
        // storage via the existing setToken() path (same mechanism the
        // email-login flow uses). The session cookie is cross-site
        // (askgully.com vs gully-api.fly.dev) and Safari ITP blocks
        // third-party cookies, so without this the login does not survive
        // a page reload on iOS. The axios interceptor sends the stored
        // token as Authorization: Bearer on subsequent requests; the
        // backend validates it against user_sessions exactly like the
        // cookie (same token, same 7-day expiry).
        if (data.session_token) await setToken(data.session_token);
        setUser(data.user);

        // Iter 54 — preserve the destination the user originally
        // intended. The Login page sent Google to `<origin><returnTo>`,
        // so `window.location.pathname` already encodes the
        // post-login destination (e.g. /plus, /chat). The legacy
        // logic forcibly sent every Google user to /onboarding or
        // /dashboard which are the pre-WhatsApp pages we're moving
        // off — that's why upgrade-clickers were landing on legacy
        // onboarding instead of returning to /plus.
        const desired = (window.location.pathname || "/chat");
        const _SAFE = ["/chat", "/plus", "/upgrade", "/reflect",
                       "/r/", "/report-card", "/me", "/share/", "/report/"];
        const safeReturn = _SAFE.some((p) => desired === p ||
                                              desired.startsWith(p + "?") ||
                                              desired.startsWith(p + "/"))
          ? desired
          : "/chat";

        const prevSessionId =
          (typeof localStorage !== "undefined")
            ? localStorage.getItem("gully_pending_continuity_session")
            : null;

        // Strip hash before navigating
        window.history.replaceState(null, "", window.location.pathname);

        if (prevSessionId) {
          try {
            await api.post("/web/continuity/link", {
              prev_session_id: prevSessionId,
            });
          } catch (linkErr) {
            // Non-blocking — user is authenticated, just no history merge.
          }
          try { localStorage.removeItem("gully_pending_continuity_session"); } catch { /* noop */ }
          navigate(safeReturn, { state: { user: data.user }, replace: true });
          return;
        }

        // Iter 54 — telemetry: fired so we can measure how many users
        // who started from the Plus page actually completed login and
        // landed back on Plus.
        try {
          if (safeReturn.startsWith("/plus") || safeReturn.startsWith("/upgrade")) {
            api.post("/client-error", {
              kind: "gully_plus_login_returned",
              session_id: null,
              payload: { return_to: safeReturn, source: "google" },
            }).catch(() => {});
          }
        } catch { /* swallow */ }

        navigate(safeReturn, { state: { user: data.user }, replace: true });
      } catch (e) {
        navigate("/login");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-loader">
      <p className="text-neutral-500 font-display text-lg">Signing you in…</p>
    </div>
  );
}
